import { type MouseEvent, useState } from 'react';
import { isNil } from 'lodash-es';
import { GenericError } from '@startuptools/ui/Error';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Stack } from '@startuptools/ui/Stack';
import { Trans, useLingui } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  useSnrShareCapitalChangesQuery,
  type GqlShareTransactionGroup,
  type GqlShareIssue,
  type GqlSnrShareCapitalChange,
  type GqlWarrantProgram,
  type GqlShareTransactionGroupQuery,
} from '../../../graphql/react-operations';
import { StgLabel } from './StgLabel';
import { SelectSccDialog } from '../../../components/simple-dialogs/SelectSccDialog';
import { GqlTransactionKind } from '../../../graphql/base-types.graphql';

export interface CustomStg extends GqlShareTransactionGroup {
  snrScc?: GqlSnrShareCapitalChange | null;
  shareIssue?: GqlShareIssue | null;
  warrantProgram?: GqlWarrantProgram | null;
}

export const Header = ({ stg }: { stg: GqlShareTransactionGroupQuery['shareTransactionGroup'] }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();

  const [selectSccDialogOpen, setSelectSccDialogOpen] = useState(false);

  const {
    data: snrSccData,
    loading: snrSccLoading,
    error: snrSccError,
  } = useSnrShareCapitalChangesQuery({ variables: { companyId } });
  const snrScc = snrSccData?.snrShareCapitalChanges;

  const foundSnrScc = snrScc?.find(scc => scc.id === stg.snrShareCapitalChangeId) || null;

  const isInterim = (stg: GqlShareTransactionGroupQuery['shareTransactionGroup']): boolean => {
    return (
      [GqlTransactionKind.Issue, GqlTransactionKind.Split].includes(stg.kind) && isNil(stg.snrShareCapitalChangeId)
    );
  };
  const handleConnectButton = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    event.preventDefault();
    setSelectSccDialogOpen(true);
  };

  const handleCloseSelectSccDialog = (
    event: MouseEvent<HTMLTableRowElement | HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>,
  ): void => {
    event.stopPropagation();
    event.preventDefault();
    setSelectSccDialogOpen(false);
  };

  if (snrSccLoading) {
    return null;
  }

  if (snrSccError) {
    return <GenericError />;
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Box display={'flex'} width={'50%'}>
          <Typography mr={3} width={'20%'}>
            <DateFormat locale={i18n.locale}>{stg.transactionDate}</DateFormat>
          </Typography>
          <StgLabel stg={stg} snrScc={foundSnrScc} />
        </Box>
        {isInterim(stg) && (
          <Button color="primary" size="sm" onClick={handleConnectButton} sx={{ marginRight: 2 }}>
            {stg.kind === GqlTransactionKind.Issue && <Trans id="Connect Share Issue" />}
            {stg.kind === GqlTransactionKind.Split && <Trans id="Connect Split" />}
          </Button>
        )}
      </Stack>
      {selectSccDialogOpen && (
        <SelectSccDialog
          onClose={handleCloseSelectSccDialog}
          stg={stg}
          kind={stg.kind === GqlTransactionKind.Split || stg.kind === GqlTransactionKind.Unissue ? stg.kind : undefined}
        />
      )}
    </>
  );
};
